import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from '@xstyled/styled-components';
import { t } from 'ttag';
import DotsIcon from '~/assets/button/dotsVertical.svg';
import { useEditorStore } from '~/shell/Store';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { duplicateTab } from '~/training/sequence/templates/items/tab/duplicateTabUtils';
import CopyGrayIcon from '~/assets/icons/copyGray.svg';
import TrashIcon from '~/assets/icons/trash.svg';
import type { TabItem } from '~/training/sequence/templates/items/tab/types';

const MenuButton = styled.button`
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;

  svg {
    flex-shrink: 0;
  }

  path {
    fill: #a9a9a9;
  }

  &:hover path {
    opacity: 0.7;
  }
`;

const Popup = styled.div<{ top: number; left: number }>`
  position: fixed;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  background-color: #fff;
  z-index: 1000;
  border-radius: 5px;
  padding: 10px 10px;
  box-shadow:
    0 -2px 4px 0 rgba(0, 0, 0, 0.05),
    0 30px 30px 0 rgba(0, 0, 0, 0.03),
    0 8px 9px 0 rgba(0, 0, 0, 0.21);
`;

const MenuItem = styled.div`
  color: #2e2e2e;
  font-family: Raleway;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 3px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #f5f5f5;
  }

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
`;

interface TabOptionsButtonProps {
  tabId: string | number;
  maxTabs: number;
  className?: string;
  tabItems?: TabItem[] | null;
}

export const TabOptionsButton: React.FC<TabOptionsButtonProps> = ({
  tabId,
  className,
  tabItems,
  maxTabs
}) => {
  const { sequence, removeSubItem, addSubItem } = useEditorStore();
  const { setActiveTab } = useSequenceStore();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        dotsRef.current &&
        !dotsRef.current.contains(event.target as Node)
      ) {
        setIsPopupOpen(false);
      }
    };

    if (isPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupOpen]);

  const handleDotsClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (dotsRef.current) {
      const rect = dotsRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom,
        left: rect.left - 50
      });
    }

    setIsPopupOpen(!isPopupOpen);
  };

  const handleDuplicateTab = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsPopupOpen(false);

    if (!sequence || !tabItems) return;

    const currentTab = tabItems.find(
      (tab: TabItem) => tab.id.toString() === tabId.toString()
    );
    if (!currentTab) return;

    const currentTabIndex = tabItems.findIndex(
      (tab: TabItem) => tab.id.toString() === tabId.toString()
    );

    const tabPositions = tabItems.map((it: TabItem) => it.position ?? 0);
    const currentPosition = currentTab.position ?? 0;

    const nextPosition =
      currentTabIndex < tabItems.length - 1
        ? currentPosition + 0.5
        : Math.max(...tabPositions) + 1;

    duplicateTab(currentTab, nextPosition, sequence.id, addSubItem);

    setActiveTab(sequence.id.toString(), currentTabIndex + 1);
  };

  const handleDeleteTab = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsPopupOpen(false);
    removeSubItem(tabId);
  };

  const tabCount = tabItems?.length ?? 0;
  const canDelete = tabCount > 1;
  const canDuplicate = tabCount < maxTabs;

  return (
    <>
      <MenuButton
        ref={dotsRef}
        type="button"
        onClick={handleDotsClick}
        aria-label={t`Tab options`}
        title={t`Tab options`}
        className={className}
      >
        <DotsIcon />
      </MenuButton>
      {isPopupOpen &&
        createPortal(
          <Popup
            ref={popupRef}
            top={popupPosition.top}
            left={popupPosition.left}
          >
            {canDuplicate && (
              <MenuItem onClick={handleDuplicateTab}>
                <CopyGrayIcon />
                {t`Duplicate`}
              </MenuItem>
            )}
            {canDelete && (
              <MenuItem onClick={handleDeleteTab}>
                <TrashIcon />
                {t`Delete`}
              </MenuItem>
            )}
          </Popup>,
          document.body
        )}
    </>
  );
};
